import { useCallback, useState } from "react";
import { useDeleteDocumentMutation } from "../../graphql/supergraphHooks";
import { clientContext } from "../../utils/graphql_utils";

/**
 * A hook that provides functionality for deleting documents
 *
 * @param caseId - The ID of the case the documents belong to
 * @returns Object containing function to delete documents and loading state
 */
export default function useDocumentDelete(caseId: number | undefined) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDocument, { loading: deleteLoading }] = useDeleteDocumentMutation({
    context: clientContext(),
    refetchQueries: ["GetDocumentDetails"],
    awaitRefetchQueries: true,
  });

  /**
   * Deletes multiple documents from the system in parallel
   * Only processes documents with valid IDs
   *
   * @param documents - Documents to delete
   */
  const deleteDocuments = useCallback(
    async (documentIds: number[]) => {
      if (documentIds.length === 0 || !caseId) {
        return;
      }

      setIsDeleting(true);
      try {
        const deletions = documentIds
          .filter((data) => !!data)
          .map(async (documentId) => {
            await deleteDocument({
              variables: {
                caseId,
                documentId,
              },
            });
          });

        await Promise.all(deletions);
      } finally {
        setIsDeleting(false);
      }
    },
    [caseId, deleteDocument],
  );

  return {
    deleteDocuments,
    loading: isDeleting || deleteLoading,
  };
}
